import { ThemeConfig } from '@/types';
import { createContext, useEffect, useId, useMemo, useState } from 'react';

interface ThemeWrapperProps {
  config: ThemeConfig | string;
  children: React.ReactNode;
}

type BookingSvgTheme = {
  bookingNameFontSize: string | number;
  bookingUnbookedNameFontSize: string | number;
  bookingExtraFontSize: string | number;
  bookingLocationFontSize: string | number;
  bookingTimeFontSize: string | number;
};

type ThemePalette = {
  cardBackground: string;
  pageForeground: string;
  paragraphForeground: string;
  headingForegrounds: {
    h1: string;
    h2: string;
    h3: string;
    h4: string;
    h5: string;
    h6: string;
  };
  bookingPrimaryForeground: string;
  bookingRowBackground: string;
  bookingRowSecondaryBackground: string;
};

export const BookingSvgThemeContext = createContext<BookingSvgTheme>({
  bookingNameFontSize: 100,
  bookingUnbookedNameFontSize: 100,
  bookingExtraFontSize: 40,
  bookingLocationFontSize: 40,
  bookingTimeFontSize: 40,
});

export const ThemePaletteContext = createContext<ThemePalette>({
  cardBackground: '#fff',
  pageForeground: '#222',
  paragraphForeground: '#222',
  headingForegrounds: {
    h1: '#222',
    h2: '#222',
    h3: '#222',
    h4: '#222',
    h5: '#222',
    h6: '#222',
  },
  bookingPrimaryForeground: '#222',
  bookingRowBackground: '#fff',
  bookingRowSecondaryBackground: '#eee',
});

function normalizeLegacyBookingFontSize(value: string | number | undefined) {
  const raw = value?.toString().trim();

  if (!raw) {
    return raw;
  }

  // Legacy values like "100viewport" were stored without a valid CSS unit.
  if (/^-?\d+(\.\d+)?viewport$/i.test(raw)) {
    return raw.replace(/viewport$/i, '');
  }

  return raw;
}

function toSvgFontSize(value: string | number | undefined, fallback: string) {
  const raw = normalizeLegacyBookingFontSize(value ?? fallback)?.toString().trim();

  if (!raw) {
    return Number(fallback);
  }

  if (!Number.isNaN(Number(raw))) {
    return Number(raw);
  }

  return raw;
}

export function ThemeWrapper({ config, children }: ThemeWrapperProps) {
  const newConfig = useMemo<ThemeConfig | null>(() => {
    if (!config) {
      return null;
    }

    if (typeof config !== 'string') {
      return config as ThemeConfig;
    }

    try {
      return JSON.parse(config) as ThemeConfig;
    } catch {
      return null;
    }
  }, [config]);

  const [unit, setUnit] = useState<'vh' | 'vw'>(() =>
    typeof window !== 'undefined' && window.innerWidth > window.innerHeight ? 'vw' : 'vh',
  );
  const rawId = useId();
  // useId produces strings like ":r0:" — strip punctuation for a valid CSS identifier
  const scopeId = 'tw-' + rawId.replace(/[^a-zA-Z0-9]/g, '');
  const s = `#${scopeId}`;

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const updateUnit = () => {
      setUnit(window.innerWidth > window.innerHeight ? 'vw' : 'vh');
    };

    updateUnit();
    window.addEventListener('resize', updateUnit);
    return () => {
      window.removeEventListener('resize', updateUnit);
    };
  }, []);

  if (!newConfig) return <>{children}</>;

  const bookingSvgTheme = {
    bookingNameFontSize: toSvgFontSize(newConfig.theme?.bookingName?.fontSize, '100'),
    bookingUnbookedNameFontSize: toSvgFontSize(newConfig.theme?.bookingUnbookedName?.fontSize ?? newConfig.theme?.bookingName?.fontSize, '100'),
    bookingExtraFontSize: toSvgFontSize(newConfig.theme?.bookingExtra?.fontSize, '40'),
    bookingLocationFontSize: toSvgFontSize(newConfig.theme?.bookingLocation?.fontSize, '40'),
    bookingTimeFontSize: toSvgFontSize(newConfig.theme?.bookingTime?.fontSize, '40'),
  };
  const bookingNameForeground = newConfig.theme?.bookingName?.foreground ?? '#000';
  const bookingUnbookedNameForeground = newConfig.theme?.bookingUnbookedName?.foreground ?? bookingNameForeground;
  const bookingExtraForeground = newConfig.theme?.bookingExtra?.foreground ?? '#000';
  const bookingLocationForeground = newConfig.theme?.bookingLocation?.foreground ?? '#000';
  const bookingTimeForeground = newConfig.theme?.bookingTime?.foreground ?? '#222';
  const bookingNameFontWeight = newConfig.theme?.bookingName?.fontWeight ?? '700';
  const bookingUnbookedNameFontWeight = newConfig.theme?.bookingUnbookedName?.fontWeight ?? bookingNameFontWeight;
  const bookingExtraFontWeight = newConfig.theme?.bookingExtra?.fontWeight ?? '500';
  const bookingLocationFontWeight = newConfig.theme?.bookingLocation?.fontWeight ?? '500';
  const bookingTimeFontWeight = newConfig.theme?.bookingTime?.fontWeight ?? '500';
  const bookingNameFontFamily = newConfig.theme?.bookingName?.fontFamily ?? newConfig.theme?.fontFamily ?? 'inherit';
  const bookingUnbookedNameFontFamily = newConfig.theme?.bookingUnbookedName?.fontFamily ?? bookingNameFontFamily;
  const bookingExtraFontFamily = newConfig.theme?.bookingExtra?.fontFamily ?? newConfig.theme?.fontFamily ?? 'inherit';
  const bookingLocationFontFamily = newConfig.theme?.bookingLocation?.fontFamily ?? newConfig.theme?.fontFamily ?? 'inherit';
  const bookingTimeFontFamily = newConfig.theme?.bookingTime?.fontFamily ?? newConfig.theme?.fontFamily ?? 'inherit';
  const bookingRowBackground = newConfig.theme?.bookingRow?.background ?? '#fff';
  const bookingRowSecondaryBackground = newConfig.theme?.bookingRow?.secondaryBackground ?? '#eee';
  const themePalette = {
    cardBackground: newConfig.theme?.card ?? '#fff',
    pageForeground: newConfig.theme?.foreground ?? '#222',
    paragraphForeground: newConfig.theme?.p?.foreground ?? newConfig.theme?.foreground ?? '#222',
    headingForegrounds: {
      h1: newConfig.theme?.h1?.foreground ?? newConfig.theme?.foreground ?? '#222',
      h2: newConfig.theme?.h2?.foreground ?? newConfig.theme?.foreground ?? '#222',
      h3: newConfig.theme?.h3?.foreground ?? newConfig.theme?.foreground ?? '#222',
      h4: newConfig.theme?.h4?.foreground ?? newConfig.theme?.foreground ?? '#222',
      h5: newConfig.theme?.h5?.foreground ?? newConfig.theme?.foreground ?? '#222',
      h6: newConfig.theme?.h6?.foreground ?? newConfig.theme?.foreground ?? '#222',
    },
    bookingPrimaryForeground: bookingNameForeground,
    bookingRowBackground,
    bookingRowSecondaryBackground,
  };

  return (
    <>
      <style>
        {`

            ${
              newConfig?.imports
                ?.map(
                  (url: string) => `
                   ${url};
                `
                )
                ?.join('\n') ?? ''
            }

            ${newConfig?.fontFaces?.join('\n') ?? ''}

          ${s}  {
                color: ${newConfig.theme?.foreground};
                background: ${newConfig.theme?.background?.startsWith('http') ? `url(${newConfig.theme?.background})` : newConfig.theme?.background};
               background-repeat: no-repeat;
                background-size: cover;
                      background-position: center center;
            }

            ${s} .text-block {
                font-family: ${newConfig.theme?.fontFamily};
                font-weight: ${newConfig.theme?.fontWeight};
                color: ${newConfig.theme?.foreground};

            }



            ${s} svg > text, ${s} svg > tspan{
                font-family: ${newConfig.theme?.fontFamily};
            }


            ${s} .card {
                background-color: ${newConfig.theme?.card};
            }

            ${s}  .text-block  p , ${s} .text-block  p > * {
                font-size: ${parseFloat(newConfig.theme?.p?.fontSize)}${unit};
                font-weight: ${newConfig.theme?.p?.fontWeight};
                color: ${newConfig.theme?.p?.foreground} ;
                font-family: ${newConfig.theme?.p?.fontFamily};
            }

            ${s} .text-block   h1, ${s} .text-block   h1 > *  {
                font-size: ${parseFloat(newConfig.theme?.h1?.fontSize)}${unit};
                font-weight: ${newConfig.theme?.h1?.fontWeight};
                color: ${newConfig.theme?.h1?.foreground};
                font-family: ${newConfig.theme?.h1?.fontFamily};
            }

            ${s} .text-block  h2 , ${s} .text-block   h2 > * {
                font-size: ${parseFloat(newConfig.theme?.h2?.fontSize)}${unit};
                font-weight: ${newConfig.theme?.h2?.fontWeight};
                color: ${newConfig.theme?.h2?.foreground};
                font-family: ${newConfig.theme?.h2?.fontFamily};
            }

            ${s} .text-block h3, ${s} .text-block h3 > * {
                font-size: ${parseFloat(newConfig.theme?.h3?.fontSize)}${unit};
                font-weight: ${newConfig.theme?.h3?.fontWeight};
                color: ${newConfig.theme?.h3?.foreground};
                font-family: ${newConfig.theme?.h3?.fontFamily};
            }

            ${s} .text-block   h4,  ${s} .text-block   h4 > * {
                font-size: ${parseFloat(newConfig.theme?.h4?.fontSize)}${unit};
                font-weight: ${newConfig.theme?.h4?.fontWeight};
                color: ${newConfig.theme?.h4?.foreground};
                font-family: ${newConfig.theme?.h4?.fontFamily};
            }

            ${s} .text-block  h5, ${s} .text-block  h5 > * {
                font-size: ${parseFloat(newConfig.theme?.h5?.fontSize)}${unit};
                font-weight: ${newConfig.theme?.h5?.fontWeight};
                color: ${newConfig.theme?.h5?.foreground};
                font-family: ${newConfig.theme?.h5?.fontFamily};
            }

            ${s} .text-block  h6, ${s} .text-block  h6 > * {
                font-size: ${parseFloat(newConfig.theme?.h6?.fontSize)}${unit};
                font-weight: ${newConfig.theme?.h6?.fontWeight};
                color: ${newConfig.theme?.h6?.foreground};
                font-family: ${newConfig.theme?.h6?.fontFamily};
            }
            ${s} .booking-block .booking-name > svg > text, ${s} .booking-name {
                font-weight: ${bookingNameFontWeight};
                fill: ${bookingNameForeground};
                color: ${bookingNameForeground};
                font-family: ${bookingNameFontFamily};
            }

            ${s} .booking-block .booking-unbooked-name > svg > text, ${s} .booking-unbooked-name {
                font-weight: ${bookingUnbookedNameFontWeight};
                fill: ${bookingUnbookedNameForeground};
                color: ${bookingUnbookedNameForeground};
                font-family: ${bookingUnbookedNameFontFamily};
            }

            ${s} .booking-block .booking-extra > svg > text, ${s} .booking-extra {
                font-weight: ${bookingExtraFontWeight};
                fill: ${bookingExtraForeground};
                color: ${bookingExtraForeground};
                font-family: ${bookingExtraFontFamily};
            }

            ${s} .booking-block .booking-location > svg > text, ${s} .booking-location {
                font-weight: ${bookingLocationFontWeight};
                fill: ${bookingLocationForeground};
                color: ${bookingLocationForeground};
                font-family: ${bookingLocationFontFamily};
            }

            ${s} .booking-block .booking-time > svg > text, ${s} .booking-time {
                font-weight: ${bookingTimeFontWeight};
                fill: ${bookingTimeForeground};
                color: ${bookingTimeForeground};
                font-family: ${bookingTimeFontFamily};
            }

            ${s} .booking-list-block .booking-name {
                font-size: ${Number(bookingSvgTheme.bookingNameFontSize) / 100 * 3}${unit};
            }
            ${s} .booking-list-block .booking-extra {
                font-size: ${Number(bookingSvgTheme.bookingExtraFontSize) / 40 * 1.5}${unit};
            }
            ${s} .booking-list-block .booking-location {
                font-size: ${Number(bookingSvgTheme.bookingLocationFontSize) / 40 * 1.5}${unit};
            }
            ${s} .booking-list-block .booking-time {
                font-size: ${Number(bookingSvgTheme.bookingTimeFontSize) / 40 * 1.5}${unit};
            }

            ${s} .booking-row:nth-child(odd) {
                background-color: ${bookingRowBackground};
            }
            ${s} .booking-row:nth-child(even) {
                background-color: ${bookingRowSecondaryBackground};
            }

            ${s} .booking-row {
                font-family: ${newConfig.theme?.bookingTime?.fontFamily ?? newConfig.theme?.fontFamily ?? 'inherit'};
            }


        `}
      </style>
      <ThemePaletteContext.Provider value={themePalette}>
        <BookingSvgThemeContext.Provider value={bookingSvgTheme}>
          <div id={scopeId} className="w-screen h-screen ">{children}</div>
        </BookingSvgThemeContext.Provider>
      </ThemePaletteContext.Provider>
    </>
  );
}
